import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Lock, Loader2, Download, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { startCheckout, formatPrice } from "../lib/checkout";
import { Reveal } from "../components/motion";

// Bilingual perks per plan (mirrors the marketing grid)
const PLAN_PERKS = {
  plan_basic: {
    fr: [
      "Revue mensuelle en PDF (~24 pages)",
      "Infolettre hebdomadaire",
      "Recettes exclusives excellentes pour le cerveau",
      "Articles rétablissement + accompagnement par les pairs",
    ],
    en: [
      "Monthly PDF magazine (~24 pages)",
      "Weekly newsletter",
      "Exclusive brain-healthy recipes",
      "Recovery + peer support articles",
    ],
  },
  plan_decouverte: {
    fr: [
      "Tout Basic",
      "Accès web aux articles exclusifs",
      "30 recettes IA/mois excellentes pour votre humeur",
      "Revue mensuelle Nordicvy en PDF incluse",
      "Dictée vocale des ingrédients",
    ],
    en: [
      "Everything in Basic",
      "Web access to exclusive articles",
      "30 AI mood-boosting recipes/month",
      "Monthly Nordicvy PDF magazine included",
      "Voice dictation of ingredients",
    ],
  },
  plan_ancrage: {
    fr: [
      "Tout Découverte",
      "1 séance mensuelle de 45 min",
      "Accompagnement pair CPMC",
      "Priorité de réservation",
    ],
    en: [
      "Everything in Découverte",
      "1 monthly 45-min session",
      "CPMC peer supporter",
      "Priority booking",
    ],
  },
  plan_envol: {
    fr: [
      "Tout Ancrage",
      "2 séances mensuelles de 60 min",
      "60 recettes/mois excellentes pour votre humeur",
      "Parcours complet de rétablissement",
    ],
    en: [
      "Everything in Ancrage",
      "2 monthly 60-min sessions",
      "60 mood-boosting recipes/month",
      "Full recovery journey",
    ],
  },
};

const ANNUAL_PRICES = {
  plan_basic: { amount: 49.99, savings_fr: "2 mois offerts", savings_en: "2 months free" },
  plan_decouverte: { amount: 99.99, savings_fr: "2 mois offerts", savings_en: "2 months free" },
};
const ANNUAL_LINKS = {
  plan_basic: "https://buy.stripe.com/dRm7sK2CagtagAq92UcZa03",
  plan_decouverte: "https://buy.stripe.com/eVqaEWccKb8Qesi4MEcZa04",
};

const PAYMENT_LINKS = {
  plan_basic: "https://buy.stripe.com/00waEWfoWb8Qcka1AscZa06",
  plan_decouverte: "https://buy.stripe.com/bJeeVcekS2Ck5VM92UcZa02",
};

const PLAN_ORDER = ["plan_basic", "plan_decouverte"];
const HIGHLIGHT = "plan_decouverte"; // ⭐

// Current & past issues (bilingual). Add new issues here each month.
const ISSUES = [
  {
    id: "oct-2026",
    month: { fr: "Octobre 2026", en: "October 2026" },
    title: { fr: "Numéro d'automne — Cerveau, ancrage et rétablissement", en: "Autumn issue — Brain, grounding and recovery" },
    cover: "https://customer-assets-0z36b82j.emergentagent.net/job_mood-food-wellness/artifacts/2fytirvq_Nordicvy.pdf",
    pdf: "https://customer-assets-0z36b82j.emergentagent.net/job_mood-food-wellness/artifacts/2fytirvq_Nordicvy.pdf",
  },
];

const Members = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);

  useEffect(() => {
    api.get("/shop/products").then(({ data }) => {
      const subs = data.filter((p) => p.category === "subscription");
      subs.sort((a, b) => PLAN_ORDER.indexOf(a.lookup_key) - PLAN_ORDER.indexOf(b.lookup_key));
      setPlans(subs);
    }).catch(() => {});
  }, []);

  const subscribe = async (lookup_key) => {
    const link = PAYMENT_LINKS[lookup_key];
    if (link) {
      window.location.href = link;
      return;
    }
    if (!user || user === false) return;
    setLoadingPlan(lookup_key);
    try {
      await startCheckout({ lookup_key, user_id: user.id });
    } catch {
      toast.error(lang === "fr" ? "Erreur lors de l'abonnement." : "Subscription error.");
      setLoadingPlan(null);
    }
  };

  // Member: unlocked content — PDF issues
  if (user && user !== false && user.is_member) {
    return (
      <div data-testid="members-unlocked" className="pt-36 pb-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-primary/70 text-xs uppercase tracking-[0.25em] mb-6">{t("members.welcome")}, {user.name}</p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tightest text-foreground mb-4">
            {lang === "fr" ? "La revue mensuelle" : "The monthly review"}
          </h1>
          <p className="text-foreground/65 font-light text-lg mb-14 max-w-2xl">
            {lang === "fr"
              ? "Téléchargez le numéro du mois. Recettes, inspirations, rétablissement et accompagnement par les pairs."
              : "Download this month's issue. Recipes, inspiration, recovery and peer support."}
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ISSUES.map((iss, i) => (
            <Reveal key={iss.id} delay={i * 0.08}>
              <div data-testid={`issue-${iss.id}`} className="bg-card border border-border rounded-3xl overflow-hidden flex flex-col">
                <div className="aspect-[3/4] bg-muted flex items-center justify-center relative overflow-hidden">
                  <FileText className="w-20 h-20 text-primary/30" strokeWidth={1} />
                  <span className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs uppercase tracking-widest px-3 py-1 rounded-full">PDF</span>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-xs uppercase tracking-wider text-primary/70 mb-2">{iss.month[lang]}</p>
                  <h3 className="font-serif text-2xl text-foreground mb-4 flex-1">{iss.title[lang]}</h3>
                  <a
                    href={iss.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`download-${iss.id}`}
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    {lang === "fr" ? "Télécharger le PDF" : "Download PDF"}
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    );
  }

  // Not member: 4-plan pricing grid
  return (
    <div data-testid="members-page" className="pt-36 pb-28 max-w-[1400px] mx-auto px-6 lg:px-10">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-primary/70 text-xs uppercase tracking-[0.25em] mb-6">
            {lang === "fr" ? "Abonnement Nordicvy" : "Nordicvy Subscription"}
          </p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tightest text-foreground mb-6">
            {lang === "fr" ? "Choisir son forfait" : "Choose your plan"}
          </h1>
          <p className="text-foreground/65 font-light text-lg">
            {lang === "fr"
              ? "Quatre paliers pour tous les budgets. Annulable à tout moment."
              : "Four tiers for every budget. Cancel anytime."}
          </p>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((p, i) => {
          const perks = PLAN_PERKS[p.lookup_key]?.[lang] || [];
          const isHighlight = p.lookup_key === HIGHLIGHT;
          return (
            <Reveal key={p.lookup_key} delay={i * 0.08}>
              <div
                data-testid={`plan-${p.lookup_key}`}
                className={`relative border p-8 flex flex-col rounded-3xl h-full ${
                  isHighlight
                    ? "bg-primary text-primary-foreground border-primary shadow-xl scale-[1.02]"
                    : "bg-card border-border"
                }`}
              >
                {isHighlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-xs uppercase tracking-widest px-4 py-1 rounded-full flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> {lang === "fr" ? "Populaire" : "Popular"}
                  </span>
                )}
                <h3 className={`font-serif text-3xl mb-2 ${isHighlight ? "" : "text-foreground"}`}>
                  {p[lang].name.split("—")[0].trim()}
                </h3>
                <p className={`text-xs uppercase tracking-widest mb-6 ${isHighlight ? "text-primary-foreground/70" : "text-primary/70"}`}>
                  {lang === "fr" ? "par mois" : "per month"}
                </p>
                <p className="font-serif text-5xl leading-none mb-8">
                  {formatPrice(p.price, p.currency)}
                  <span className={`text-base font-sans font-normal ${isHighlight ? "text-primary-foreground/60" : "text-foreground/50"}`}>
                    {" "}CAD
                  </span>
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  {perks.map((perk, j) => (
                    <li
                      key={j}
                      className={`flex items-start gap-3 text-sm ${isHighlight ? "text-primary-foreground/90" : "text-foreground/75"}`}
                    >
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isHighlight ? "text-secondary" : "text-primary"}`} />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
                {SUBSCRIPTIONS_COMING_SOON ? (
                  <div className="space-y-2">
                    <div
                      data-testid={`subscribe-${p.lookup_key}-soon`}
                      className={`py-4 text-sm font-medium text-center rounded-full border ${
                        isHighlight
                          ? "border-primary-foreground/30 text-primary-foreground/80 bg-primary-foreground/5"
                          : "border-border text-foreground/60 bg-muted"
                      }`}
                    >
                      {lang === "fr" ? "Bientôt disponible" : "Coming soon"}
                    </div>
                    {ANNUAL_PRICES[p.lookup_key] && (
                      <p className={`text-xs text-center py-2 ${isHighlight ? "text-primary-foreground/50" : "text-foreground/40"}`}>
                        {lang === "fr"
                          ? `Annuel ${ANNUAL_PRICES[p.lookup_key].amount.toFixed(2).replace(".",",")} $ — bientôt`
                          : `Yearly $${ANNUAL_PRICES[p.lookup_key].amount.toFixed(2)} — soon`}
                      </p>
                    )}
                  </div>
                ) : PAYMENT_LINKS[p.lookup_key] ? (
                  <div className="space-y-2">
                    <a
                      href={PAYMENT_LINKS[p.lookup_key]}
                      data-testid={`subscribe-${p.lookup_key}`}
                      className={`block py-4 text-sm font-medium text-center active:scale-95 transition-transform rounded-full ${
                        isHighlight
                          ? "bg-secondary text-secondary-foreground hover:bg-secondary/90"
                          : "bg-primary text-primary-foreground hover:bg-primary/90"
                      }`}
                    >
                      {lang === "fr" ? "S'abonner — mensuel" : "Subscribe — monthly"}
                    </a>
                    {ANNUAL_PRICES[p.lookup_key] && (
                      ANNUAL_LINKS[p.lookup_key] ? (
                        <a
                          href={ANNUAL_LINKS[p.lookup_key]}
                          data-testid={`subscribe-${p.lookup_key}-annual`}
                          className={`block py-3 text-xs font-medium text-center active:scale-95 transition-transform rounded-full border ${
                            isHighlight
                              ? "border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10"
                              : "border-primary/60 text-primary hover:bg-primary/5"
                          }`}
                        >
                          {lang === "fr"
                            ? `Annuel · ${ANNUAL_PRICES[p.lookup_key].amount.toFixed(2).replace(".",",")} $ · ${ANNUAL_PRICES[p.lookup_key].savings_fr}`
                            : `Yearly · $${ANNUAL_PRICES[p.lookup_key].amount.toFixed(2)} · ${ANNUAL_PRICES[p.lookup_key].savings_en}`}
                        </a>
                      ) : (
                        <p className={`text-xs text-center py-2 ${isHighlight ? "text-primary-foreground/60" : "text-foreground/50"}`}>
                          {lang === "fr"
                            ? `Annuel ${ANNUAL_PRICES[p.lookup_key].amount.toFixed(2).replace(".",",")} $ — bientôt`
                            : `Yearly $${ANNUAL_PRICES[p.lookup_key].amount.toFixed(2)} — soon`}
                        </p>
                      )
                    )}
                  </div>
                ) : user && user !== false ? (
                  <button
                    data-testid={`subscribe-${p.lookup_key}`}
                    onClick={() => subscribe(p.lookup_key)}
                    disabled={loadingPlan === p.lookup_key}
                    className={`py-4 text-sm font-medium flex items-center justify-center gap-2 active:scale-95 transition-[transform,background-color] duration-200 disabled:opacity-60 rounded-full ${
                      isHighlight
                        ? "bg-secondary text-secondary-foreground hover:bg-secondary/90"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`}
                  >
                    {loadingPlan === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "fr" ? "S'abonner" : "Subscribe")}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    data-testid={`login-to-subscribe-${p.lookup_key}`}
                    className={`py-4 text-sm font-medium text-center active:scale-95 transition-transform rounded-full ${
                      isHighlight
                        ? "bg-secondary text-secondary-foreground"
                        : "bg-primary text-primary-foreground"
                    }`}
                  >
                    {lang === "fr" ? "Se connecter pour s'abonner" : "Log in to subscribe"}
                  </Link>
                )}
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-16 border border-dashed border-border p-12 text-center max-w-2xl mx-auto rounded-3xl">
          <Lock className="w-8 h-8 text-primary/40 mx-auto mb-4" />
          <p className="font-serif text-2xl text-foreground mb-2">
            {lang === "fr" ? "Contenu réservé aux abonnés" : "Subscriber-only content"}
          </p>
          <p className="text-foreground/55 text-sm">
            {lang === "fr"
              ? "La revue mensuelle en PDF et les recettes excellentes pour votre humeur sont accessibles après abonnement."
              : "The monthly PDF magazine and mood-boosting recipes are unlocked after subscribing."}
          </p>
        </div>
      </Reveal>
    </div>
  );
};

export default Members;
